"use client";

import type { TemplateListItem } from "@/features/calendar/types/templates";
import type { DateRange } from "@/features/calendar/store/useCalendarStore";
import type { LessonHost } from "@/features/calendar/types/create-slot.ts";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";
import { addDays, endOfWeek, format, isBefore, isToday, startOfWeek, toDate } from "date-fns";
import { Icon } from "@iconify-icon/react";
import { Check, Loader2, X } from "lucide-react";

import { useCalendarStore } from "@/features/calendar/store/useCalendarStore";
import { toast } from "@/utils/customToast";
import {
  getHostingMethodNameAndIcon,
  type PreviewSlot,
  slideUpAnimation,
  staggeredContainerAnimation,
} from "@/features/calendar/utils/slot-creation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/features/calendar/components/dialog.tsx";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useTemplatesStore } from "@/features/calendar/store/useTemplatesStore";
import { createSlot } from "@/features/calendar/api/Slots";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale";
import { cn } from "@/lib/utils";
import { useHostingOptions } from "@/features/calendar/types/hostingOptions";
import GenericLoader from "@/components/GenericLoader";
import { useIsMobile } from "@/hooks/use-mobile.tsx";

type StepType = "options" | "preview" | "creation";

export default function TemplateApplyDialog() {
  const { t, i18n } = useTranslation(["calendar", "common"]);
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateListItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: format(new Date(), "yyyy-MM-dd"),
    to: format(new Date(), "yyyy-MM-dd"),
  });
  const [previewSlots, setPreviewSlots] = useState<PreviewSlot[]>([]);
  const [selectedDates, setSelectedDates] = useState<Date[] | undefined>(undefined);
  const [hasErrors, setHasErrors] = useState(false);
  const [currentStep, setCurrentStep] = useState<StepType>("options");

  const { templates, fetchTemplates, isLoadingTemplates, fetchTemplateSlots } = useTemplatesStore();
  const { calendarData, setToReload } = useCalendarStore();
  const hostingOptions = useHostingOptions();

  // Get calendar end date
  const calendarEndDate = calendarData?.calendarEndsAt
    ? toDate(calendarData.calendarEndsAt)
    : addDays(new Date(), 90); // Default to 90 days if not specified

  // Listen for the custom event to open the dialog
  useEffect(() => {
    const handleOpenDialog = (event: CustomEvent<{ dateRange: DateRange }>) => {
      const { dateRange } = event.detail;

      // Convert string dates to Date objects if needed
      const fromDate =
        typeof dateRange.from === "string" ? new Date(dateRange.from) : dateRange.from;

      // Get the full week range
      const weekStart = startOfWeek(fromDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(fromDate, { weekStartsOn: 1 });

      setDateRange({
        from: format(weekStart, "yyyy-MM-dd"),
        to: format(weekEnd, "yyyy-MM-dd"),
      });

      setOpen(true);
    };

    window.addEventListener("openTemplateApplyDialog", handleOpenDialog as EventListener);

    return () => {
      window.removeEventListener("openTemplateApplyDialog", handleOpenDialog as EventListener);
    };
  }, []);

  // Fetch templates when dialog opens
  useEffect(() => {
    if (open) {
      fetchTemplates();
      setCurrentStep("options");
      setSelectedTemplate(null);
      setPreviewSlots([]);
      setSelectedDates(undefined);
      setHasErrors(false);
    }
  }, [open, fetchTemplates]);

  // Remove the useEffect that fetches template slots when a template is selected
  // (Remove the entire useEffect block that contains fetchTemplateSlots(selectedTemplate.id))

  // Add a loading state for preview slots
  const [isLoadingPreviewSlots, setIsLoadingPreviewSlots] = useState(false);

  // Format time based on locale
  const formatTime = (time: string) => {
    try {
      // Parse the time string (HH:mm)
      const [hours, minutes] = time.split(":").map(Number);

      // Create a date object with the time
      const date = new Date();

      date.setHours(hours, minutes, 0, 0);

      // Format the time according to the user's locale
      return new Intl.DateTimeFormat(i18n.language, {
        hour: "numeric",
        minute: "2-digit",
        hour12: i18n.language === "en",
      }).format(date);
    } catch (error) {
      // Fallback to original time format if parsing fails
      return time;
    }
  };

  // Modify the handleTemplateSelect function to clear preview slots when selecting a new template
  const handleTemplateSelect = (template: TemplateListItem) => {
    setSelectedTemplate(template);
    setPreviewSlots([]); // Clear preview slots when selecting a new template

    // Reset selected dates for daily templates
    if (template.type === "daily") {
      setSelectedDates([]);
    }
  };

  // Update the handleProceedToPreview function to properly handle template slot fetching and preview generation
  const handleProceedToPreview = async () => {
    if (!selectedTemplate) return;

    if (selectedTemplate.type === "daily" && (!selectedDates || selectedDates.length === 0)) {
      toast.error(t("calendar:templates.selectDatesFirst"));

      return;
    }

    // Clear any existing preview slots before transitioning
    setPreviewSlots([]);

    // Immediately transition to preview step
    setCurrentStep("preview");

    // Start loading and fetch template slots
    setIsLoadingPreviewSlots(true);

    try {
      await fetchTemplateSlots(selectedTemplate.id);

      // After fetching slots, manually generate preview slots
      // We need to access the latest templateSlots from the store
      const currentTemplateSlots = useTemplatesStore.getState().templateSlots;

      if (!currentTemplateSlots.length) {
        return;
      }

      const now = new Date();
      const tomorrow = addDays(now, 1);

      tomorrow.setHours(0, 0, 0, 0);

      const newPreviewSlots: PreviewSlot[] = [];

      if (selectedTemplate.type === "weekly") {
        // For weekly templates, map slots to the current week
        currentTemplateSlots.forEach((slot) => {
          if (!slot.day_of_week) return;

          // Calculate the date for this day of week in the current week
          const slotDate = addDays(dateRange.from, slot.day_of_week - 1);
          let endDate = slotDate;
          const [startHour, startMinute] = slot.start_time.split(":").map(Number);
          const [endHour, endMinute] = slot.end_time.split(":").map(Number);

          if (endHour < startHour || (endHour === startHour && endMinute < startMinute)) {
            endDate = addDays(endDate, 1);
          }

          // Skip if the date is in the past
          if (isBefore(slotDate, tomorrow) && !isToday(slotDate)) return;

          const previewSlot: PreviewSlot = {
            id: `preview-${slot.id}`,
            start_time: slot.start_time,
            end_time: slot.end_time,
            lesson_host: slot.lesson_host,
            communication_details: slot.communication_details || undefined,
            date: format(slotDate, "yyyy-MM-dd"),
            end_date: format(endDate, "yyyy-MM-dd"),
            status: "pending",
            overlap_gc: false,
          };

          newPreviewSlots.push(previewSlot);
        });
      } else if (selectedTemplate.type === "daily" && selectedDates && selectedDates.length > 0) {
        // For daily templates, apply to selected dates
        selectedDates.forEach((date) => {
          // Skip if the date is in the past
          if (isBefore(date, tomorrow) && !isToday(date)) return;

          currentTemplateSlots.forEach((slot) => {
            const previewSlot: PreviewSlot = {
              id: `preview-${slot.id}-${format(date, "yyyy-MM-dd")}`,
              start_time: slot.start_time,
              end_time: slot.end_time,
              lesson_host: slot.lesson_host,
              communication_details: slot.communication_details || undefined,
              date: format(date, "yyyy-MM-dd"),
              end_date: format(date, "yyyy-MM-dd"),
              status: "pending",
              overlap_gc: false,
            };

            newPreviewSlots.push(previewSlot);
          });
        });
      }

      setPreviewSlots(newPreviewSlots);
    } catch (error) {
      console.error("Failed to fetch template slots:", error);
      toast.error(t("common:errors.genericErrorTitle"));
    } finally {
      setIsLoadingPreviewSlots(false);
    }
  };

  // Handle going back to options step
  const handleBackToOptions = () => {
    setCurrentStep("options");
  };

  const handleSubmit = async () => {
    if (!selectedTemplate || previewSlots.length === 0) return;

    setIsSubmitting(true);
    setHasErrors(false);

    try {
      setCurrentStep("creation");

      // Create new slots
      const updatedPreviewSlots = [...previewSlots];
      let hasCreationErrors = false;

      for (let i = 0; i < updatedPreviewSlots.length; i++) {
        const slot = updatedPreviewSlots[i];

        try {
          if (!slot.date) continue;

          const reqData = {
            start_time: `${slot.date} ${slot.start_time}`,
            end_time: `${slot.end_date || slot.date} ${slot.end_time}`,
            lesson_host: slot.lesson_host as LessonHost,
            overlap_gc: slot.overlap_gc ? 1 : 0,
            communication_details: slot.communication_details,
          };

          await createSlot(reqData);

          // Update status
          updatedPreviewSlots[i] = {
            ...slot,
            status: "success",
          };

          setPreviewSlots([...updatedPreviewSlots]);
        } catch (error: any) {
          console.error(`Failed to create slot:`, error);
          hasCreationErrors = true;

          // Update status with error
          updatedPreviewSlots[i] = {
            ...slot,
            status: "error",
            error: error.response?.data?.message || "Failed to create",
          };

          setPreviewSlots([...updatedPreviewSlots]);
        }
      }

      // Set error flag if any slots failed to create
      setHasErrors(hasCreationErrors);

      // Trigger calendar reload
      setToReload(true);

      toast.success(t("calendar:templates.templateApplied"), {
        description: hasCreationErrors
          ? t("calendar:templates.someSlotsCreatedWithErrors")
          : t("calendar:templates.slotsCreatedSuccessfully"),
      });

      // Close dialog after successful creation only if there are no errors
      if (!hasCreationErrors) {
        setTimeout(() => {
          setOpen(false);
        }, 1500);
      }
    } catch (error) {
      console.error("Failed to apply template:", error);
      setHasErrors(true);
      toast.error(t("common:error"), {
        description: t("calendar:templates.errorApplyingTemplate"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset state when dialog closes
  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setSelectedTemplate(null);
      setPreviewSlots([]);
      setSelectedDates(undefined);
      setCurrentStep("options");
      setHasErrors(false);
      setOpen(false);
    }
  };

  // Render a slot card
  const renderSlotCard = (slot: PreviewSlot, index: number, isCreationStep = false) => {
    const { icon, name } = getHostingMethodNameAndIcon(slot.lesson_host, hostingOptions);

    return (
      <motion.div
        key={`${isCreationStep ? "creation" : "preview"}-slot-${slot.id}`}
        animate={{ opacity: 1, y: 0 }}
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
      >
        <Card className="bg-foreground overflow-hidden border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="bg-muted/70 flex size-8 items-center justify-center rounded-lg sm:size-9">
                  <Icon
                    className="text-accent-foreground"
                    height={isMobile ? "0.9rem" : "1.1rem"}
                    icon="tabler:calendar-time"
                    width={isMobile ? "0.9rem" : "1.1rem"}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                    <h3 className="text-accent-foreground text-sm font-medium sm:text-base">
                      {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                    </h3>
                    <Badge className="bg-sidebar/30 text-accent-foreground text-xs">
                      {slot.date &&
                        format(new Date(slot.date), isMobile ? "MMM d" : "PPP", {
                          locale: getDateFnsLocale(i18n.language),
                        })}
                    </Badge>
                  </div>
                  <div className="mt-1 flex items-center gap-1 sm:gap-2">
                    <p className="text-muted-foreground flex items-center gap-1 text-xs sm:text-sm">
                      <span className="text-muted-foreground mx-0.5 text-xs">•</span>
                      <span className="flex items-center gap-1 truncate">
                        <span className="size-3.5 sm:size-4">{icon}</span>
                        <span className="truncate">{name}</span>
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="ml-2 flex items-center">
                {isSubmitting || isCreationStep ? (
                  <>
                    {slot.status === "pending" && (
                      <div className="bg-muted/70 flex h-7 w-7 items-center justify-center rounded-full sm:h-8 sm:w-8">
                        <Loader2 className="text-muted-foreground h-4 w-4 animate-spin sm:h-5 sm:w-5" />
                      </div>
                    )}
                    {slot.status === "success" && (
                      <motion.div
                        animate={{ scale: 1, opacity: 1 }}
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-green-500/20 sm:h-8 sm:w-8"
                        initial={{ scale: 0.5, opacity: 0 }}
                      >
                        <Check className="h-4 w-4 text-green-500 sm:h-5 sm:w-5" />
                      </motion.div>
                    )}
                    {slot.status === "error" && (
                      <motion.div
                        animate={{ scale: 1, opacity: 1 }}
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-red-500/20 sm:h-8 sm:w-8"
                        initial={{ scale: 0.5, opacity: 0 }}
                        title={slot.error}
                      >
                        <X className="h-4 w-4 text-red-500 sm:h-5 sm:w-5" />
                      </motion.div>
                    )}
                  </>
                ) : (
                  <div className="bg-muted/70 flex h-7 w-7 items-center justify-center rounded-full sm:h-8 sm:w-8">
                    <Icon
                      className="text-muted-foreground"
                      height={isMobile ? "0.9rem" : "1.1rem"}
                      icon="tabler:clock"
                      width={isMobile ? "0.9rem" : "1.1rem"}
                    />
                  </div>
                )}
              </div>
            </div>
            {slot.status === "error" && slot.error && (
              <motion.div
                key={`error-message-${slot.id}`}
                animate={{ opacity: 1, height: "auto" }}
                className="mt-2"
                initial={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="rounded-md border border-red-500/20 bg-red-500/10 p-2 text-xs text-red-500 sm:p-3 sm:text-sm">
                  <div className="flex items-start">
                    <Icon
                      className="mt-0.5 mr-1.5 flex-shrink-0 sm:mr-2"
                      height={isMobile ? "0.8rem" : "1rem"}
                      icon="tabler:alert-circle"
                      width={isMobile ? "0.8rem" : "1rem"}
                    />
                    <span>{slot.error}</span>
                  </div>
                </div>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent
        aria-describedby={undefined}
        className="bg-background absolute! left-0 flex h-dvh max-w-full flex-col gap-0 rounded-e-none! border-none p-0 focus-visible:ring-0 focus-visible:ring-offset-0 md:left-[35%] md:max-w-[65%] xl:left-[60%] xl:max-w-[40vw]"
      >
        <motion.div
          animate={{ x: 0, opacity: 1 }}
          className="flex h-full flex-col"
          exit={{ x: 100, opacity: 0 }}
          initial={{ x: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <DialogHeader className="border-border border-b bg-transparent px-4 py-3 sm:px-6 sm:py-4">
            <DialogTitle className="text-accent-foreground text-lg font-semibold sm:text-xl">
              {currentStep === "options" && t("calendar:templates.applyTemplate")}
              {currentStep === "preview" && t("calendar:templates.previewSlots")}
              {currentStep === "creation" && t("calendar:templates.creatingSlots")}
            </DialogTitle>
          </DialogHeader>

          <AnimatePresence mode="wait">
            {currentStep === "options" && (
              <motion.div
                key="options-step"
                animate={{ opacity: 1 }}
                className="flex flex-grow flex-col"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <ScrollArea className="h-[calc(100dvh_-_61px)]">
                  <AnimatePresence mode="popLayout">
                    <motion.div
                      key="options-form"
                      animate="animate"
                      className="h-full space-y-4 p-4 sm:space-y-5 sm:p-6"
                      exit="exit"
                      initial="initial"
                      variants={staggeredContainerAnimation}
                    >
                      {/* Template Selection */}
                      <motion.div variants={slideUpAnimation}>
                        <Card className="bg-foreground overflow-hidden border-0 shadow-sm">
                          <CardContent className="relative p-4 sm:p-5">
                            <h3 className="text-accent-foreground mb-3 text-base font-medium sm:mb-4 sm:text-lg">
                              {t("calendar:templates.selectTemplate")}
                            </h3>

                            {isLoadingTemplates ? (
                              <GenericLoader
                                className={
                                  "fixed top-0 left-0 flex h-full items-center justify-center"
                                }
                              />
                            ) : templates.length === 0 ? (
                              <div className="bg-muted/50 rounded-md p-3 text-center sm:p-4">
                                <p className="text-muted-foreground text-sm">
                                  {t("calendar:templates.noTemplatesFound")}
                                </p>
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2.5">
                                {templates
                                  .filter((template) => template.slots_count > 0)
                                  .map((template) => (
                                    <motion.div
                                      key={template.id}
                                      className={cn(
                                        "hover:bg-muted flex cursor-pointer items-center justify-between rounded-md border p-2.5 transition-all sm:p-3",
                                        selectedTemplate?.id === template.id &&
                                          "border-accent-foreground bg-muted",
                                      )}
                                      onClick={() => handleTemplateSelect(template)}
                                    >
                                      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                                        <div className="bg-muted/70 flex size-7 items-center justify-center rounded-lg sm:size-9">
                                          <Icon
                                            className="text-accent-foreground"
                                            height={isMobile ? "0.9rem" : "1.1rem"}
                                            icon={
                                              template.type === "weekly"
                                                ? "tabler:calendar-week"
                                                : "tabler:calendar"
                                            }
                                            width={isMobile ? "0.9rem" : "1.1rem"}
                                          />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                          <p className="text-accent-foreground truncate text-sm font-medium sm:text-base">
                                            {template.name}
                                          </p>
                                          <p className="text-muted-foreground text-xs">
                                            {template.type === "weekly"
                                              ? t("calendar:templates.weeklyTemplate")
                                              : t("calendar:templates.dailyTemplate")}
                                          </p>
                                        </div>
                                      </div>
                                      <Badge className="bg-sidebar/30 text-accent-foreground ml-1 text-xs whitespace-nowrap">
                                        {template.slots_count}{" "}
                                        <span className="xs:inline hidden">
                                          {t("calendar:templates.slots")}
                                        </span>
                                      </Badge>
                                    </motion.div>
                                  ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>

                      {/* Date Selection for Daily Templates */}
                      {selectedTemplate && selectedTemplate.type === "daily" && (
                        <motion.div variants={slideUpAnimation}>
                          <Card className="bg-foreground overflow-hidden border-0 shadow-sm">
                            <CardContent className="p-4 sm:p-5">
                              <div className="mb-3 flex items-center justify-between sm:mb-4">
                                <h3 className="text-accent-foreground text-base font-medium sm:text-lg">
                                  {t("calendar:templates.selectDates")}
                                </h3>
                                {selectedDates && selectedDates.length > 0 && (
                                  <Badge className="bg-sidebar/30 text-accent-foreground text-xs">
                                    {selectedDates.length}{" "}
                                    <span className="xs:inline hidden">
                                      {t("calendar:templates.datesSelected")}
                                    </span>
                                  </Badge>
                                )}
                              </div>

                              <div className="flex justify-center">
                                <Calendar
                                  className="border-muted/60 max-w-full rounded-md border p-2 sm:p-3"
                                  fromDate={addDays(new Date(), 1)}
                                  locale={getDateFnsLocale(i18n.language)}
                                  mode="multiple"
                                  selected={selectedDates}
                                  toDate={calendarEndDate}
                                  weekStartsOn={1}
                                  onSelect={setSelectedDates as any}
                                />
                              </div>

                              {selectedDates && selectedDates.length > 0 && (
                                <div className="bg-muted/50 mt-3 rounded-md p-2.5 sm:mt-4 sm:p-3">
                                  <div className="flex items-center">
                                    <Icon
                                      className="text-muted-foreground mr-1.5 sm:mr-2"
                                      height={isMobile ? "0.9rem" : "1rem"}
                                      icon="tabler:calendar-check"
                                      width={isMobile ? "0.9rem" : "1rem"}
                                    />
                                    <p className="text-muted-foreground text-xs sm:text-sm">
                                      {t("calendar:templates.selectedDatesCount", {
                                        count: selectedDates.length,
                                      })}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </motion.div>
                      )}

                      {/* Week Range for Weekly Templates */}
                      {selectedTemplate && selectedTemplate.type === "weekly" && (
                        <motion.div variants={slideUpAnimation}>
                          <Card className="bg-foreground overflow-hidden border-0 shadow-sm">
                            <CardContent className="p-4 sm:p-5">
                              <h3 className="text-accent-foreground mb-3 text-base font-medium sm:mb-4 sm:text-lg">
                                {t("calendar:templates.weekRange")}
                              </h3>

                              <div className="bg-muted flex items-center justify-between rounded-md p-2.5 sm:p-3">
                                <div className="flex items-center gap-2 sm:gap-3">
                                  <div className="bg-muted/70 flex size-7 items-center justify-center rounded-lg sm:size-9">
                                    <Icon
                                      className="text-accent-foreground"
                                      height={isMobile ? "0.9rem" : "1.1rem"}
                                      icon="tabler:calendar-week"
                                      width={isMobile ? "0.9rem" : "1.1rem"}
                                    />
                                  </div>
                                  <div>
                                    <p className="text-accent-foreground text-sm font-medium sm:text-base">
                                      {format(dateRange.from, isMobile ? "MMM d" : "PPP", {
                                        locale: getDateFnsLocale(i18n.language),
                                      })}{" "}
                                      -{" "}
                                      {format(dateRange.to, isMobile ? "MMM d" : "PPP", {
                                        locale: getDateFnsLocale(i18n.language),
                                      })}
                                    </p>
                                    <p className="text-muted-foreground text-xs sm:text-sm">
                                      {t("calendar:templates.weekRangeDescription")}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      )}
                    </motion.div>
                  </AnimatePresence>

                  <motion.div
                    key="options-actions"
                    animate={{ opacity: 1, y: 0 }}
                    className="border-border transparent sticky bottom-0 p-4 pt-0 sm:pt-0"
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="mt-4 flex justify-end sm:mt-6">
                      <Button
                        disabled={
                          !selectedTemplate ||
                          (selectedTemplate.type === "daily" &&
                            (!selectedDates || selectedDates.length === 0))
                        }
                        size={isMobile ? "sm" : "default"}
                        variant={"foreground"}
                        onClick={handleProceedToPreview}
                      >
                        <div className="flex items-center gap-1.5">
                          <Icon
                            height={isMobile ? "16" : "18"}
                            icon="tabler:eye"
                            width={isMobile ? "16" : "18"}
                          />
                          <span>{t("calendar:templates.previewSlots")}</span>
                        </div>
                      </Button>
                    </div>
                  </motion.div>
                </ScrollArea>
              </motion.div>
            )}

            {currentStep === "preview" && (
              <motion.div
                key="preview-step"
                animate={{ opacity: 1 }}
                className="flex flex-grow flex-col"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <ScrollArea className="h-[calc(100dvh_-_120px)]">
                  <div className="space-y-3 p-4 sm:space-y-4 sm:p-6">
                    {isLoadingPreviewSlots ? (
                      <div className="flex items-center justify-center py-8">
                        <GenericLoader />
                      </div>
                    ) : (
                      <div className="space-y-2 sm:space-y-3">
                        {previewSlots.length === 0 ? (
                          <Card className="bg-foreground overflow-hidden border-0 shadow-sm">
                            <CardContent className="p-4 text-center sm:p-5">
                              <p className="text-muted-foreground text-sm">
                                {t("calendar:templates.noSlotsToPreview")}
                              </p>
                            </CardContent>
                          </Card>
                        ) : (
                          previewSlots.map((slot, index) => renderSlotCard(slot, index))
                        )}
                      </div>
                    )}
                  </div>
                </ScrollArea>

                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="border-border bg-background flex h-14 items-center justify-between border-t px-4 py-1 sm:h-16 sm:px-6"
                  initial={{ opacity: 0, y: 20 }}
                  transition={{ delay: 0.3 }}
                >
                  <Button
                    disabled={isSubmitting}
                    size={isMobile ? "sm" : "default"}
                    variant="outline"
                    onClick={handleBackToOptions}
                  >
                    <div className="flex items-center gap-1.5">
                      <Icon
                        height={isMobile ? "16" : "18"}
                        icon="tabler:arrow-left"
                        width={isMobile ? "16" : "18"}
                      />
                      <span>{t("common:back")}</span>
                    </div>
                  </Button>

                  <Button
                    disabled={isSubmitting || isLoadingPreviewSlots || previewSlots.length === 0}
                    size={isMobile ? "sm" : "default"}
                    variant={"foreground"}
                    onClick={handleSubmit}
                  >
                    <div className="flex items-center gap-1.5">
                      <Icon
                        height={isMobile ? "16" : "18"}
                        icon="tabler:calendar-plus"
                        width={isMobile ? "16" : "18"}
                      />
                      <span>{t("calendar:templates.applyTemplate")}</span>
                    </div>
                  </Button>
                </motion.div>
              </motion.div>
            )}

            {currentStep === "creation" && (
              <motion.div
                key="creation-step"
                animate={{ opacity: 1 }}
                className="flex flex-grow flex-col"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <ScrollArea className="h-[calc(100dvh_-_120px)]">
                  <div className="space-y-4 p-4 sm:space-y-5 sm:p-6">
                    <motion.div
                      animate={{ opacity: 1, y: 0 }}
                      initial={{ opacity: 0, y: 20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Card className="bg-foreground mb-4 overflow-hidden border-0 shadow-sm sm:mb-5">
                        <CardContent className="p-4 sm:p-5">
                          <div className="flex flex-col items-center justify-center py-3 text-center sm:py-4">
                            <div className="bg-muted mb-3 flex h-12 w-12 items-center justify-center rounded-full sm:mb-4 sm:h-14 sm:w-14">
                              {isSubmitting ? (
                                <Icon
                                  className="text-accent-foreground animate-pulse"
                                  height={isMobile ? "1.5rem" : "1.75rem"}
                                  icon="tabler:calendar-plus"
                                  width={isMobile ? "1.5rem" : "1.75rem"}
                                />
                              ) : hasErrors ? (
                                <Icon
                                  className="text-red-500"
                                  height={isMobile ? "1.5rem" : "1.75rem"}
                                  icon="tabler:alert-triangle"
                                  width={isMobile ? "1.5rem" : "1.75rem"}
                                />
                              ) : (
                                <Icon
                                  className="text-green-500"
                                  height={isMobile ? "1.5rem" : "1.75rem"}
                                  icon="tabler:check"
                                  width={isMobile ? "1.5rem" : "1.75rem"}
                                />
                              )}
                            </div>
                            <h3 className="text-accent-foreground text-lg font-medium sm:text-xl">
                              {isSubmitting
                                ? t("calendar:templates.creatingSlots")
                                : hasErrors
                                  ? t("calendar:templates.creationWithErrors")
                                  : t("calendar:templates.creationComplete")}
                            </h3>
                            <p className="text-muted-foreground mt-1 max-w-md text-sm sm:mt-2">
                              {isSubmitting
                                ? t("calendar:templates.creatingSlotsPleaseWait")
                                : hasErrors
                                  ? t("calendar:templates.creationErrorsDescription")
                                  : t("calendar:templates.creationSuccessDescription")}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>

                    <div className="space-y-2 sm:space-y-3">
                      {previewSlots.map((slot, index) => renderSlotCard(slot, index, true))}
                    </div>
                  </div>
                </ScrollArea>

                {!isSubmitting && (
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    className="border-border bg-background flex h-14 items-center justify-end border-t px-4 py-1 sm:h-16 sm:px-6"
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Button
                      size={isMobile ? "sm" : "default"}
                      variant={"foreground"}
                      onClick={() => setOpen(false)}
                    >
                      <div className="flex items-center gap-1.5">
                        <Icon
                          height={isMobile ? "16" : "18"}
                          icon="tabler:check"
                          width={isMobile ? "16" : "18"}
                        />
                        <span>{t("common:done")}</span>
                      </div>
                    </Button>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
